package org.teddapp.project.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.layout.Layout
import kotlin.math.roundToInt

/**
 * Un en-tête qui s'efface quand on fait défiler le contenu, et revient dès qu'on remonte.
 *
 * Sur téléphone, un bloc de tête fixe (solde, résumé, minuteur) ne laisse au contenu
 * qu'une bande étroite. Ici le contenu garde son propre défilement, mais l'en-tête lui
 * cède sa place : faire défiler vers le bas le pousse hors de l'écran, et le contenu
 * prend toute la hauteur.
 */
@Composable
fun TeddCollapsingLayout(
    header: @Composable () -> Unit,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    var headerHeight by remember { mutableIntStateOf(0) }
    // De 0 (en-tête entier) à -headerHeight (en-tête sorti).
    var offset by remember { mutableFloatStateOf(0f) }

    val connection = remember {
        object : NestedScrollConnection {
            // L'en-tête passe avant le contenu, dans les deux sens : il se replie d'abord
            // quand on descend, et reparaît d'abord quand on remonte.
            override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset {
                val next = (offset + available.y).coerceIn(-headerHeight.toFloat(), 0f)
                val consumed = next - offset
                offset = next
                return Offset(0f, consumed)
            }
        }
    }

    Layout(
        content = {
            Box { header() }
            Box { content() }
        },
        modifier = modifier.clipToBounds().nestedScroll(connection),
    ) { measurables, constraints ->
        val head = measurables[0].measure(constraints.copy(minHeight = 0, maxHeight = androidx.compose.ui.unit.Constraints.Infinity))
        headerHeight = head.height
        // Un en-tête qui rétrécit (changement d'onglet) ne doit pas laisser un décalage périmé.
        if (offset < -head.height) offset = -head.height.toFloat()
        val shift = offset.roundToInt()
        val visible = (head.height + shift).coerceAtLeast(0)
        val height = if (constraints.hasBoundedHeight) constraints.maxHeight else head.height
        val body = measurables[1].measure(
            constraints.copy(minHeight = 0, maxHeight = (height - visible).coerceAtLeast(0)),
        )
        layout(constraints.maxWidth, height) {
            body.place(0, visible)
            head.place(0, shift)
        }
    }
}
